
import React, { useState, useMemo, useEffect } from 'react';
import { format } from 'date-fns';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { CheckCircle, Trash2, Calendar, GraduationCap, Clock, Users, Pencil } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { ScrollArea } from '@/components/ui/scroll-area';
import { BASE_URL } from '@/config/api';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { cn } from '@/lib/utils';
import { eventService } from '@/services/eventService';

const EventsActionsModal = ({
  mode,
  open,
  onOpenChange,
  events,
  onSubmit
}) => {
  const { t } = useLanguage();
  const { token, user } = useAuth();
  const { toast } = useToast();
  const isMobile = useIsMobile();

  const [loading, setLoading] = useState(false);
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [selectedTab, setSelectedTab] = useState("all"); // State for tab selection
  const [dropdownStates, setDropdownStates] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    if (selectedTab === "all") {
      setSelectedEvents(filteredEvents.map(event => event.id));
    } else {
      // When tab changes, only select events of that type
      setSelectedEvents(filteredEvents
        .filter(event => event.type === parseInt(selectedTab, 10))
        .map(event => event.id));
    }
  }, [selectedTab, events]);

    /*const filteredEvents = useMemo(() => {
        return events.filter(event => [10, 100].includes(event.type) && (selectedTab === "all" || event.type === parseInt(selectedTab, 10)));
    }, [events, selectedTab]);*/

    const filteredEvents = useMemo(() => {
  const now = new Date();

  return events.filter(event => {
    const isAllowedType = [10, 100].includes(event.type);
    const matchesTab = selectedTab === "all" || event.type === parseInt(selectedTab, 10);

    const hasEnded = new Date(event.end) <= now;
    const hasCompleted = event.extendedProps.completed;

    // Mode "complete" : doit être terminé ET non complété
    if (mode === 'complete') {
      if (!hasEnded || hasCompleted) return false;
    }

    // Mode "delete" : ne doit PAS être terminé ET ne doit PAS être complété
    if (mode === 'delete') {
      if (hasCompleted) return false;
    }

    return isAllowedType && matchesTab;
  });
}, [events, selectedTab, mode]);




  const handleEventSelection = (eventId) => {
    setSelectedEvents(prev => {
      if (prev.includes(eventId)) {
        return prev.filter(id => id !== eventId);
      } else {
        return [...prev, eventId];
      }
    });
  };

  const isEventSelected = (eventId) => selectedEvents.includes(eventId);

  const toggleDropdown = (eventId) => {
    setDropdownStates(prev => ({
      ...prev,
      [eventId]: !prev[eventId]
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const eventIds = selectedEvents;

      if (!eventIds || eventIds.length === 0) {
        toast({
          title: "No events selected",
          description: "Please select at least one event to proceed.",
          variant: "destructive"
        });
        setLoading(false);
        return;
      }

      if (mode === 'delete') {
        await eventService.deleteEvents(token, eventIds);
      } else {
        await eventService.markEventsAsCompleted(token, eventIds);
      }

      if (onSubmit) {
        onSubmit(eventIds);
      }

      onOpenChange(false);

      toast({
        title: `Events ${mode === 'delete' ? 'deleted' : 'marked as completed'}`,
        description: `Selected events have been successfully ${mode === 'delete' ? 'deleted' : 'marked as completed'}.`,
      });
    } catch (error) {
      console.error(`Error ${mode} events:`, error);
      toast({
        title: `Error ${mode} events`,
        description: error instanceof Error ? error.message : "Please try again",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={isMobile ? "p-0 w-full h-[100dvh] max-h-[100dvh]" : "max-w-[600px]"}>
        <DialogHeader className={isMobile ? "px-6 py-4 border-b mt-1" : "pb-5"}>
          <DialogTitle>
            {mode === 'complete' ? (
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-500" />
                {t('agenda.markAsCompleted')}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Trash2 className="h-5 w-5 text-red-500" />
                {t('agenda.deleteEvents')}
              </div>
            )}
          </DialogTitle>
          {/*<DialogDescription>
            {mode === 'complete'
              ? t('agenda.selectEventsToMarkAsCompleted')
              : t('agenda.selectEventsToDelete')}
          </DialogDescription>*/}
        </DialogHeader>

        <ScrollArea className={isMobile ? "max-h-[calc(100dvh-180px)]" : "max-h-[60vh]"}>
          <div className={`grid gap-4 py-4 ${isMobile ? "px-6" : ""}`}>
            {/* Tab selection for event types */}
            <RadioGroup 
              className="flex space-x-4 mb-6"
              value={selectedTab}
              onValueChange={setSelectedTab}
            >
              <div>
                <RadioGroupItem 
                  value="all" 
                  id="all-tab"
                  className="sr-only"
                />
                <Label
                  htmlFor="all-tab"
                  className={cn(
                    "flex items-center justify-center px-4 py-2 rounded-md border transition-all",
                    selectedTab === "all" 
                      ? "bg-primary text-primary-foreground border-primary font-medium shadow-sm" 
                      : "bg-background hover:bg-muted/50"
                  )}
                >
                  {t('agenda.all')}
                </Label>
              </div>
              <div>
                <RadioGroupItem 
                  value="10" 
                  id="lessons-tab"
                  className="sr-only"
                />
                <Label
                  htmlFor="lessons-tab"
                  className={cn(
                    "flex items-center justify-center px-4 py-2 rounded-md border transition-all",
                    selectedTab === "10" 
                      ? "bg-primary text-primary-foreground border-primary font-medium shadow-sm" 
                      : "bg-background hover:bg-muted/50"
                  )}
                >
                  {t('agenda.lessons')}
                </Label>
              </div>
              <div>
                <RadioGroupItem 
                  value="100" 
                  id="events-tab"
                  className="sr-only"
                />
                <Label
                  htmlFor="events-tab"
                  className={cn(
                    "flex items-center justify-center px-4 py-2 rounded-md border transition-all",
                    selectedTab === "100" 
                      ? "bg-primary text-primary-foreground border-primary font-medium shadow-sm" 
                      : "bg-background hover:bg-muted/50"
                  )}
                >
                  {t('agenda.events')}
                </Label>
              </div>
            </RadioGroup>

            {filteredEvents.length === 0 ? (
              <div className="text-muted-foreground">
                {t('agenda.noEventsFound')}
              </div>
            ) : (
              <div className="space-y-3">
                {filteredEvents.map(event => {
                  const students = event.extendedProps.students || [];
                  const studentCount = students.length;
                  const start = new Date(event.start);
                  const end = new Date(event.end);
                  const isAllDay = event.allDay;
                  const category = event.extendedProps.category || '';
                  const duration = event.extendedProps.duration || '';
                  const location = event.extendedProps.location;
                  const teacherName = event.extendedProps.teacherName || '';

                  return (
                    <div
                      key={event.id}
                      className={cn(
                        "relative flex flex-col rounded-lg border p-4 transition-colors cursor-pointer",
                        isEventSelected(event.id) ? "border-primary bg-primary/5" : "border-muted hover:border-muted-foreground/20"
                      )}
                      onClick={() => handleEventSelection(event.id)}
                    >
                      <div className="flex items-center justify-between mt-2">
                        {/* Event type badge */}
                        <div className="flex items-center">
                          {/*<span 
                            className={`text-xs px-2 py-1 rounded-full mr-2 ${event.type === 10 ? 'bg-green-100 text-green-800' : event.type === 51 ? 'bg-blue-100 text-blue-800' : 'bg-gray-200 text-gray-800'}`}
                          >
                            {event.type === 10 ? t('agenda.lessons') : event.type === 51 ? t('agenda.events') : event.type}
                          </span>*/}
                          {/* Category badge */}

                          {category && !isAllDay && (
                            <span 
                              className="text-xs px-2 py-1 rounded-full text-white mr-2" 
                              style={{ backgroundColor: event.backgroundColor }}
                            >
                              {category}
                            </span>
                          )}

                          {category && isAllDay && (
                            <span 
                              className="text-xs px-2 py-1 rounded-full text-white mr-2" 
                              style={{ backgroundColor: "#888" }}
                            >
                              {t('agenda.event')}
                            </span>
                          )}
                        </div>
                        
                        {/* Selection indicator */}
                        <div className={`h-5 w-5 rounded-full border-2 flex items-center justify-center ${isEventSelected(event.id) ? 'bg-blue-500 border-blue-500' : 'border-gray-300'}`}>
                          {isEventSelected(event.id) && (
                            <CheckCircle className="h-4 w-4 text-white" />
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-1 gap-2">
                        {event.title && event.title !== "Event" && (
                          <div className="flex items-start space-x-2 mt-2">
                            <Pencil className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                            <span className='text-xs'>{event.title}</span>
                          </div>
                        )}
                        {/* Left column */}
                        <div>
                          {/* Date and time */}
                          <div className="flex items-start space-x-2 mt-2">
                            <Calendar className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                            <span className='text-sm'>
                              {isAllDay 
                                ? format(start, 'MMM d, yyyy') + ' - ' + t('agenda.allDay') 
                                : `${format(start, 'MMM d, yyyy - HH:mm')} - ${format(end, 'HH:mm')}`}
                            </span>
                          </div>

                          {/* Duration */}
                          {duration && !isAllDay && (
                            <div className="flex items-center space-x-2 mt-2">
                              <Clock className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                              <span className="text-sm">{duration} {t('agenda.minutes')}</span>
                            </div>
                          )}


                          {/* Location */}
                          {location && (
                            <div className="flex items-start space-x-2 mt-2">
                              <svg 
                                xmlns="http://www.w3.org/2000/svg" 
                                className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" 
                                fill="none" 
                                viewBox="0 0 24 24" 
                                stroke="currentColor"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                              <span className="text-sm">{location}</span>
                            </div>
                          )}
                        </div>

                        {/* Right column - Students */}
                        <div>
                          {/* Teacher Name (for school) */}
                          {teacherName && user.account_type === "S" && (
                            <div className="flex items-center space-x-2 mt-2">
                              <GraduationCap className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                              <span className="text-sm">{teacherName}</span>
                            </div>
                          )}

                          {/* Student(s) */}
                          <div className="flex items-start space-x-2">
                            <Users className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                            <div>
                              {studentCount === 0 ? (
                                <span className="text-sm text-muted-foreground">{t('agenda.noStudents')}</span>
                              ) : studentCount === 1 ? (
                                <span className="text-sm">{students[0].name}</span>
                              ) : (
                                <div className="relative">
                                  <div 
                                    className="text-sm font-medium flex items-center cursor-pointer"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleDropdown(event.id);
                                    }}
                                  >
                                    <span>{students[0].name}</span>
                                    <span className="ml-1">+{studentCount - 1}</span>
                                    <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={dropdownStates[event.id] ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"}></path>
                                    </svg>
                                  </div>
                                  
                                  {dropdownStates[event.id] && (
                                    <div 
                                      className="absolute left-0 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-10 p-1"
                                      onClick={e => e.stopPropagation()}
                                    >
                                      {students.slice(1).map((student, index) => (
                                        <div key={index} className="px-3 py-1.5 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 rounded-sm">
                                          {student.name}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </ScrollArea>

        <DialogFooter className={isMobile ? "px-6 py-4 border-t sticky bottom-0 bg-gray-100 z-20" : "pt-5"}>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button
            variant={mode === 'delete' ? "destructive" : "default"}
            onClick={handleSubmit}
            disabled={selectedEvents.length === 0 || loading}
          >
            {loading ? t('common.processing') : (
              mode === 'complete'
                ? `${t('agenda.markAsCompleted')} (${selectedEvents.length})`
                : `${t('agenda.delete')} (${selectedEvents.length})`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EventsActionsModal;
